import { formatDate } from '@parallel-fluidics/shared/shipping';

/**
 * Parse a lineItem id of the form `<skuOrProjectId>_<orderId>_<shipIdx>_<lineItemIdx>`.
 * Parsed from the end — SKUs may legally contain underscores.
 * Returns null when the id doesn't match the expected shape.
 */
export function parseLineItemId(lineItemId) {
  if (typeof lineItemId !== 'string') return null;
  const segments = lineItemId.split('_');
  if (segments.length < 4) return null;
  const orderId = segments[segments.length - 3];
  const shipIdx = Number.parseInt(segments[segments.length - 2], 10);
  const lineItemIdx = Number.parseInt(segments[segments.length - 1], 10);
  if (!orderId || Number.isNaN(shipIdx) || Number.isNaN(lineItemIdx)) return null;
  return { orderId, shipIdx, lineItemIdx };
}

/**
 * Batch number `YYYYMMDD-XXX`.
 * Date comes from order.createdAt (server timestamp), falling back to
 * order.orderDate ("YYYY-MM-DD") for orders created before createdAt existed.
 * XXX is the 1-based position of the traveler's lineItem in the flattened
 * order.shipments[].lineItems[] arrays, zero-padded to 3 digits.
 * Returns '' when the batch can't be computed (caller lets the user fill it in).
 */
export function getBatchNumber(order, lineItemId) {
  const parsed = parseLineItemId(lineItemId);
  if (!order || !parsed) return '';

  let datePart = '';
  if (order.createdAt?.toDate) {
    datePart = formatDate(order.createdAt.toDate()).replaceAll('-', '');
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(order.orderDate || '')) {
    datePart = order.orderDate.replaceAll('-', '');
  }
  if (!datePart) return '';

  const shipments = order.shipments || [];
  const { shipIdx, lineItemIdx } = parsed;
  const lineItems = shipments[shipIdx]?.lineItems;
  if (!lineItems || lineItemIdx >= lineItems.length) return '';

  const priorCount = shipments
    .slice(0, shipIdx)
    .reduce((sum, shipment) => sum + (shipment.lineItems?.length ?? 0), 0);
  const sequence = priorCount + lineItemIdx + 1;
  return `${datePart}-${String(sequence).padStart(3, '0')}`;
}
